import api from './api';

export interface IncomeSource {
  id: number;
  family_id: string;
  name: string;
  icon: string;
  color: string;
  description: string | null;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface SourceWallet {
  id: number;
  family_id: string;
  source_id: number;
  name: string;
  current_balance: number;
  initial_balance: number;
  description: string | null;
  is_active: boolean;
  created_by: number;
  source_name: string;
  source_icon: string;
  source_color: string;
  created_at: string;
  updated_at: string;
}

export interface IncomeTransaction {
  id: number;
  family_id: string;
  source_id: number;
  wallet_id: number;
  amount: number;
  transaction_type: 'income';
  income_date: string;
  notes: string | null;
  created_by: number;
  source_name: string;
  source_icon: string;
  source_color: string;
  wallet_name: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

export interface IncomeStatistics {
  sourceStats: Array<{
    id: number;
    name: string;
    icon: string;
    color: string;
    total_income: number;
    total_expense: number;
    transaction_count: number;
  }>;
  totalStats: {
    totalIncome: number;
    totalExpense: number;
    netIncome: number;
  };
  walletBalances: Array<{
    name: string;
    current_balance: number;
    source_name: string;
    source_icon: string;
    source_color: string;
  }>;
  monthlyStats: Array<{
    month: string;
    total_income: number;
    total_expense: number;
    transaction_count: number;
  }>;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Utility function to format currency
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    minimumFractionDigits: 0,
  }).format(amount);
};

export const incomeService = {
  // ===== INCOME SOURCES =====
  
  // Lấy danh sách nguồn thu nhập
  getSources: async (): Promise<IncomeSource[]> => {
    const response = await api.get('/api/income/sources');
    return response.data.data || [];
  },

  // Tạo nguồn thu nhập mới
  createSource: async (data: {
    name: string;
    icon?: string;
    color?: string;
    description?: string;
  }): Promise<IncomeSource> => {
    const response = await api.post('/api/income/sources', data);
    return response.data.data;
  },

  // Cập nhật nguồn thu nhập
  updateSource: async (id: number, data: {
    name?: string;
    icon?: string;
    color?: string;
    description?: string;
    is_active?: boolean;
  }): Promise<IncomeSource> => {
    const response = await api.put(`/api/income/sources/${id}`, data);
    return response.data.data;
  },

  // Xóa nguồn thu nhập
  deleteSource: async (id: number): Promise<void> => {
    await api.delete(`/api/income/sources/${id}`);
  },

  // ===== SOURCE WALLETS =====

  // Lấy danh sách ví nguồn
  getWallets: async (): Promise<SourceWallet[]> => {
    const response = await api.get('/api/income/wallets');
    return response.data.data || [];
  },

  // Tạo ví nguồn mới
  createWallet: async (data: {
    source_id: number;
    name: string;
    initial_balance?: number;
    description?: string;
  }): Promise<SourceWallet> => {
    const response = await api.post('/api/income/wallets', data);
    return response.data.data;
  },

  // Cập nhật ví nguồn
  updateWallet: async (id: number, data: {
    name?: string;
    description?: string;
    is_active?: boolean;
  }): Promise<SourceWallet> => {
    const response = await api.put(`/api/income/wallets/${id}`, data);
    return response.data.data;
  },

  // Xóa ví nguồn
  deleteWallet: async (id: number): Promise<void> => {
    await api.delete(`/api/income/wallets/${id}`);
  },

  // ===== INCOME TRANSACTIONS =====

  // Lấy danh sách giao dịch thu nhập
  getTransactions: async (params?: {
    page?: number;
    limit?: number;
    source_id?: number;
    wallet_id?: number;
    transaction_type?: 'income';
    start_date?: string;
    end_date?: string;
    created_by?: number;
    search?: string;
  }): Promise<PaginatedResponse<IncomeTransaction>> => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/api/income/transactions?${queryParams}`);
    return response.data;
  },

  // Tạo giao dịch thu nhập mới
  createTransaction: async (data: {
    source_id: number;
    wallet_id: number;
    amount: number;
    transaction_type: 'income';
    income_date: string;
    notes?: string;
  }): Promise<IncomeTransaction> => {
    const response = await api.post('/api/income/transactions', data);
    return response.data.data;
  },

  // Cập nhật giao dịch thu nhập
  updateTransaction: async (id: number, data: {
    source_id?: number;
    wallet_id?: number;
    amount?: number;
    transaction_type?: 'income';
    income_date?: string;
    notes?: string;
  }): Promise<IncomeTransaction> => {
    const response = await api.put(`/api/income/transactions/${id}`, data);
    return response.data.data;
  },

  // Xóa giao dịch thu nhập
  deleteTransaction: async (id: number): Promise<void> => {
    await api.delete(`/api/income/transactions/${id}`);
  },

  // ===== INCOME STATISTICS =====

  // Lấy thống kê thu nhập
  getStatistics: async (params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<IncomeStatistics> => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/api/income/statistics?${queryParams}`);
    return response.data.data;
  },

  // ===== WALLET TRANSFER =====

  // Chuyển khoản giữa các ví
  transferWallet: async (data: {
    from_wallet_id: number;
    to_wallet_id: number;
    amount: number;
    transfer_date: string;
    notes?: string;
  }): Promise<any> => {
    const response = await api.post('/api/income/wallets/transfer', data);
    return response.data;
  },

  // Lấy lịch sử chuyển khoản
  getTransferHistory: async (params?: {
    page?: number;
    limit?: number;
    wallet_id?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<PaginatedResponse<IncomeTransaction>> => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/api/income/wallets/transfer-history?${queryParams}`);
    return response.data;
  },

  // ===== WALLET OVERVIEW =====

  // Lấy tổng quan về ví
  getWalletOverview: async (): Promise<any> => {
    const response = await api.get('/api/income/wallets/overview');
    return response.data.data;
  },

  // Lấy chi tiết ví
  getWalletDetails: async (walletId: number): Promise<any> => {
    const response = await api.get(`/api/income/wallets/${walletId}/details`);
    return response.data.data;
  }
}; 